import { useEffect, useState } from "react";import { AdminShell } from "@/components/Layout";
import { api, formatApiErrorDetail } from "@/lib/api";
import { toast } from "sonner";
import { CurrencyDollar, Warning, Plus, X, Trash, Lock, Bank, Envelope, Gavel, Receipt, LinkSimple, UploadSimple } from "@phosphor-icons/react";

const money = (c) => `$${((c || 0) / 100).toLocaleString(undefined, { minimumFractionDigits: 2, maximumFractionDigits: 2 })}`;
const bucketMeta = {
  current:   { label: "Current",   bg: "bg-emerald-600", text: "on time" },
  b1_30:     { label: "1–30 days", bg: "bg-amber-500",   text: "late" },
  b31_60:    { label: "31–60",     bg: "bg-orange-600",  text: "notice" },
  b61_90:    { label: "61–90",     bg: "bg-red-600",     text: "critical" },
  b90_plus:  { label: "90+",       bg: "bg-slate-900",   text: "action now" },
};

export default function DelinquencyRadar() {
  const [entries, setEntries] = useState([]);
  const [summary, setSummary] = useState(null);
  const [canEdit, setCanEdit] = useState(false);
  const [filter, setFilter] = useState("");
  const [addOpen, setAdd] = useState(false);
  const [payOpen, setPay] = useState(null);
  const [achOpen, setAch] = useState(null);
  const [escOpen, setEsc] = useState(null);
  const [importOpen, setImportOpen] = useState(false);

  const load = async () => {
    try {
      const [e, s] = await Promise.all([
        api.get("/rent-roll"), api.get("/delinquency/summary"),
      ]);
      setEntries(e.data.entries || []);
      setCanEdit(!!e.data.can_edit);
      setSummary(s.data);
    } catch { toast.error("Load failed"); }
  };
  useEffect(() => { load(); }, []);

  const filtered = filter ? entries.filter(e => e.bucket === filter) : entries;

  const del = async (id) => {
    if (!window.confirm("Remove this rent-roll entry? Payment history will be deleted.")) return;
    try { await api.delete(`/rent-roll/${id}`); toast.success("Removed"); load(); }
    catch (e) { toast.error(formatApiErrorDetail(e?.response?.data?.detail ?? "Failed")); }
  };

  const reminder = async (id) => {
    try {
      const r = await api.post(`/rent-roll/${id}/reminder`);
      toast.success(`Reminder logged for ${r.data.sent_to || "tenant"}`);
    } catch (e) { toast.error(formatApiErrorDetail(e?.response?.data?.detail ?? "Failed")); }
  };

  return (
    <AdminShell>
      <div className="p-4 md:p-6 space-y-4" data-testid="delinquency-radar-page">
        <div>
          <h1 className="text-4xl font-black uppercase tracking-widest flex items-center gap-2">
            <CurrencyDollar weight="fill" className="text-emerald-500"/> Delinquency Radar
          </h1>
          <p className="text-sm text-slate-600 mt-2 max-w-3xl">
            Rent roll, live balances, aging buckets, one-tap reminders, and an ACH-pull button (stubbed until Stripe launch). Escalate any row to a full eviction case with one click — court date auto-posts to the Team Calendar.
          </p>
        </div>

        <div className="p-2 bg-amber-50 border-l-4 border-amber-500 text-[11px] flex items-center gap-2">
          <Warning size={14} className="text-amber-600"/>
          <span><b>ACH is a placeholder.</b> All ACH buttons log a stub payment — no real bank transfer yet. Fill in Stripe/Plaid credentials after launch to activate.</span>
        </div>

        {!canEdit && (
          <div className="p-2 bg-red-50 border-l-4 border-red-500 text-[11px] flex items-center gap-2">
            <Lock size={14}/> Read-only — owner-level access required to add or edit.
          </div>
        )}

        {summary && (
          <div className="grid grid-cols-2 md:grid-cols-6 gap-2" data-testid="delinquency-summary">
            <StatCard label="Active Units" value={summary.active_units} bg="bg-slate-700" onClick={() => setFilter("")}/>
            <StatCard label="Total Owed"  value={money(summary.total_owed_cents)} bg="bg-emerald-700" onClick={() => setFilter("")}/>
            <StatCard label="Current"  value={summary.buckets?.current || 0}  bg={bucketMeta.current.bg}
              active={filter === "current"} onClick={() => setFilter(filter === "current" ? "" : "current")} testid="filter-current"/>
            <StatCard label="1–30 days" value={summary.buckets?.b1_30 || 0}   bg={bucketMeta.b1_30.bg}
              active={filter === "b1_30"} onClick={() => setFilter(filter === "b1_30" ? "" : "b1_30")} testid="filter-b1_30"/>
            <StatCard label="31–60"    value={summary.buckets?.b31_60 || 0}  bg={bucketMeta.b31_60.bg}
              active={filter === "b31_60"} onClick={() => setFilter(filter === "b31_60" ? "" : "b31_60")} testid="filter-b31_60"/>
            <StatCard label="60+"       value={(summary.buckets?.b61_90 || 0) + (summary.buckets?.b90_plus || 0)} bg={bucketMeta.b61_90.bg}
              active={filter === "b61_90" || filter === "b90_plus"}
              onClick={() => setFilter(filter === "b61_90" ? "b90_plus" : filter === "b90_plus" ? "" : "b61_90")}
              testid="filter-severe"/>
          </div>
        )}

        {canEdit && (
          <div className="flex gap-2 flex-wrap">
            <button onClick={() => setAdd(true)} data-testid="new-rent-roll-btn"
              className="h-10 px-3 bg-emerald-600 text-white font-black uppercase tracking-widest text-[11px] shadow-[3px_3px_0_#0f172a] inline-flex items-center gap-1">
              <Plus size={14}/> Add Unit / Tenant
            </button>
            <button onClick={() => setImportOpen(true)} data-testid="import-xlsx-btn"
              className="h-10 px-3 bg-slate-900 text-white font-black uppercase tracking-widest text-[11px] shadow-[3px_3px_0_#0f172a] inline-flex items-center gap-1">
              <UploadSimple size={14}/> Import Appfolio XLSX
            </button>
          </div>
        )}

        {filtered.length === 0 ? (
          <div className="rugged-card p-6 text-center text-sm text-slate-500">
            {entries.length === 0 ? "No rent roll entries yet — add your first unit." : "Nothing in this bucket."}
          </div>
        ) : (
          <div className="space-y-2">
            {filtered.map(e => (
              <Row key={e.id} e={e} canEdit={canEdit}
                onDel={() => del(e.id)}
                onPay={() => setPay(e)}
                onAch={() => setAch(e)}
                onReminder={() => reminder(e.id)}
                onEscalate={() => setEsc(e)}
              />
            ))}
          </div>
        )}

        {addOpen && <RentRollModal onClose={() => setAdd(false)} onSaved={() => { setAdd(false); load(); }}/>}
        {importOpen && <ImportXlsxModal onClose={() => setImportOpen(false)} onDone={() => { setImportOpen(false); load(); }}/>}
        {payOpen && <PaymentModal entry={payOpen} onClose={() => setPay(null)} onSaved={() => { setPay(null); load(); }}/>}
        {achOpen && <AchModal entry={achOpen} onClose={() => setAch(null)} onSaved={() => { setAch(null); load(); }}/>}
        {escOpen && <EscalateModal entry={escOpen} onClose={() => setEsc(null)} onSaved={() => { setEsc(null); load(); toast.success("Eviction case opened"); }}/>}
      </div>
    </AdminShell>
  );
}

function StatCard({ label, value, bg, active, onClick, testid }) {
  return (
    <button onClick={onClick} data-testid={testid}
      className={`p-3 ${bg} text-white shadow-[3px_3px_0_#0f172a] text-left ${active ? "ring-4 ring-yellow-400" : ""}`}>
      <div className="text-[10px] font-black uppercase tracking-widest opacity-70">{label}</div>
      <div className="text-2xl font-black">{value}</div>
    </button>
  );
}

function Row({ e, canEdit, onDel, onPay, onAch, onReminder, onEscalate }) {
  const b = bucketMeta[e.bucket] || bucketMeta.current;
  return (
    <div className="p-3 rugged-card space-y-2" data-testid={`rent-row-${e.id}`}>
      <div className="flex items-start justify-between gap-2 flex-wrap">
        <div className="min-w-0 flex-1">
          <div className="flex items-center gap-2 flex-wrap">
            <div className="font-black text-sm">{e.tenant_name}</div>
            <span className={`px-2 h-6 leading-6 ${b.bg} text-white font-black uppercase tracking-widest text-[9px]`}>
              {b.label}
            </span>
            {e.ach_configured && (
              <span className="px-2 h-6 leading-6 bg-cyan-700 text-white font-black uppercase tracking-widest text-[9px] inline-flex items-center gap-1">
                <Bank size={10}/> ACH • {e.ach_last4}
              </span>
            )}
            {!e.active && <span className="text-[9px] uppercase text-slate-400">(inactive)</span>}
          </div>
          <div className="text-[10px] text-slate-600">
            {e.property_address}{e.unit && ` · Unit ${e.unit}`} · Rent {money(e.monthly_rent_cents)}/mo, due day {e.due_day}
          </div>
          <div className="text-[11px] mt-1">
            Balance: <b className={e.balance_cents > 0 ? "text-red-700" : "text-emerald-700"}>{money(e.balance_cents)}</b>
            {e.days_past_due > 0 && <> · <b>{e.days_past_due}d</b> past due</>}
            {e.months_behind > 0 && <> · {e.months_behind}mo behind</>}
            {e.last_payment_at && <> · Last pmt {e.last_payment_at.slice(0, 10)} ({money(e.last_payment_amount_cents)})</>}
          </div>
          {e.tenant_email && <div className="text-[10px] text-cyan-700">{e.tenant_email}{e.tenant_phone && ` · ${e.tenant_phone}`}</div>}
          {e.notes && <div className="text-[11px] text-slate-700 mt-1">{e.notes}</div>}
        </div>
        {canEdit && (
          <div className="flex gap-1 flex-wrap shrink-0">
            <button onClick={onPay} data-testid={`row-pay-${e.id}`}
              className="h-8 px-2 bg-emerald-600 text-white font-black uppercase tracking-widest text-[9px] inline-flex items-center gap-1">
              <Receipt size={11}/> Record Pmt
            </button>
            <button onClick={onAch} data-testid={`row-ach-${e.id}`}
              className="h-8 px-2 bg-cyan-700 text-white font-black uppercase tracking-widest text-[9px] inline-flex items-center gap-1">
              <Bank size={11}/> {e.ach_configured ? "ACH Pull" : "Setup ACH"}
            </button>
            <button onClick={onReminder} data-testid={`row-remind-${e.id}`}
              className="h-8 px-2 bg-amber-500 text-white font-black uppercase tracking-widest text-[9px] inline-flex items-center gap-1">
              <Envelope size={11}/> Remind
            </button>
            <button onClick={() => {
              const url = `${window.location.origin}/pay/${e.pay_token}`;
              navigator.clipboard.writeText(url).then(() => toast.success("Pay link copied"))
                .catch(() => toast.info(url));
            }} data-testid={`row-paylink-${e.id}`}
              className="h-8 px-2 bg-slate-800 text-white font-black uppercase tracking-widest text-[9px] inline-flex items-center gap-1"
              disabled={!e.pay_token}>
              <LinkSimple size={11}/> Pay Link
            </button>
            <button onClick={onEscalate} data-testid={`row-escalate-${e.id}`}
              className="h-8 px-2 bg-red-600 text-white font-black uppercase tracking-widest text-[9px] inline-flex items-center gap-1">
              <Gavel size={11}/> Escalate
            </button>
            <button onClick={onDel} className="h-8 w-8 border-2 border-red-600 text-red-600 hover:bg-red-50 inline-flex items-center justify-center">
              <Trash size={12}/>
            </button>
          </div>
        )}
      </div>
    </div>
  );
}

function ModalShell({ title, bg, onClose, children, testid }) {
  return (
    <div className="fixed inset-0 bg-slate-900/60 z-[80] flex items-center justify-center p-4 overflow-y-auto" data-testid={testid}>
      <div className="bg-white w-full max-w-lg border-4 border-slate-900 shadow-[6px_6px_0_#0f172a] my-4">
        <div className={`p-3 ${bg} text-white flex items-start justify-between sticky top-0`}>
          <div className="font-black uppercase tracking-widest">{title}</div>
          <button onClick={onClose}><X size={18}/></button>
        </div>
        <div className="p-4 space-y-2 max-h-[80vh] overflow-y-auto">{children}</div>
      </div>
    </div>
  );
}

function ImportXlsxModal({ onClose, onDone }) {
  const [file, setFile] = useState(null);
  const [rows, setRows] = useState([]);       // editable preview
  const [stats, setStats] = useState(null);   // {parsed, will_create, will_skip}
  const [bulkRent, setBulkRent] = useState("");
  const [importedCount, setImportedCount] = useState(0);
  const [busy, setBusy] = useState(false);

  const refreshImportedCount = async () => {
    try {
      const r = await api.get("/rent-roll/appfolio-imports/count");
      setImportedCount(r.data.count || 0);
    } catch { /* silent */ }
  };
  useEffect(() => { refreshImportedCount(); }, []);

  const dryRun = async (f) => {
    if (!f) return;
    setFile(f); setBusy(true);
    try {
      const fd = new FormData();
      fd.append("file", f);
      const r = await api.post("/rent-roll/import-xlsx?dry_run=true", fd,
        { headers: { "Content-Type": "multipart/form-data" }});
      // Make rows editable — add monthly_rent (whole dollars) field on each row.
      setRows((r.data.rows || []).map(x => ({ ...x, monthly_rent: "" })));
      setStats({ parsed: r.data.parsed, will_create: r.data.will_create, will_skip: r.data.will_skip });
    } catch (e) {
      toast.error(formatApiErrorDetail(e?.response?.data?.detail ?? "Could not parse file"));
      setRows([]); setStats(null);
    } finally { setBusy(false); }
  };

  const applyBulkRent = () => {
    const dollars = parseFloat(bulkRent);
    if (isNaN(dollars) || dollars < 0) return toast.error("Enter a dollar amount");
    setRows(rows.map(r => r.will_skip ? r : { ...r, monthly_rent: String(dollars) }));
    toast.success(`Set rent to $${dollars.toFixed(0)} on ${rows.filter(r => !r.will_skip).length} new rows`);
  };

  const confirm = async () => {
    const toCreate = rows.filter(r => !r.will_skip);
    if (toCreate.length === 0) return;
    setBusy(true);
    try {
      const payload = {
        rows: toCreate.map(r => ({
          property_address: r.property_address,
          unit: r.unit || "",
          tenant_name: r.tenant_name,
          move_in: r.move_in || "",
          deposit_cents: r.deposit_cents || 0,
          portal_activated: !!r.portal_activated,
          insurance_expires: r.insurance_expires || "",
          monthly_rent_cents: Math.max(0, Math.round((parseFloat(r.monthly_rent) || 0) * 100)),
        })),
      };
      const res = await api.post("/rent-roll/import-confirm", payload);
      toast.success(`Created ${res.data.created} · Skipped ${res.data.skipped}`);
      onDone();
    } catch (e) { toast.error(formatApiErrorDetail(e?.response?.data?.detail ?? "Import failed")); }
    finally { setBusy(false); }
  };

  const undoAll = async () => {
    if (!window.confirm(`Delete ALL ${importedCount} Appfolio-imported tenants from your rent roll?\n\nThis removes only rows created by the importer. Manually-added tenants are safe.`)) return;
    setBusy(true);
    try {
      const r = await api.delete("/rent-roll/appfolio-imports");
      toast.success(`Removed ${r.data.deleted} imported tenants`);
      setImportedCount(0);
      onDone();
    } catch (e) { toast.error(formatApiErrorDetail(e?.response?.data?.detail ?? "Failed")); }
    finally { setBusy(false); }
  };

  const willCreateCount = rows.filter(r => !r.will_skip).length;

  return (
    <ModalShell title="Import Appfolio Tenant XLSX" bg="bg-slate-900" onClose={onClose} testid="import-xlsx-modal">
      <div className="text-[11px] text-slate-600 leading-snug">
        Upload an Appfolio <b>Tenant Directory</b> export (.xlsx). Preview each row, optionally set the
        monthly rent, and click confirm. Duplicates are skipped automatically.
      </div>

      {!stats && (
        <label className="block cursor-pointer border-2 border-dashed border-slate-400 p-6 text-center bg-slate-50 hover:bg-slate-100" data-testid="import-xlsx-drop">
          <UploadSimple size={28} className="mx-auto text-slate-500"/>
          <div className="mt-2 font-black uppercase tracking-widest text-xs text-slate-800">
            {busy ? "Parsing…" : "Tap to choose xlsx"}
          </div>
          <div className="text-[10px] text-slate-500 mt-1">Max 5 MB</div>
          <input type="file" accept=".xlsx" className="hidden" data-testid="import-xlsx-input"
            onChange={e => dryRun(e.target.files?.[0])} disabled={busy}/>
        </label>
      )}

      {stats && (
        <div className="space-y-2">
          <div className="grid grid-cols-3 gap-2">
            <div className="p-2 bg-slate-100 text-center">
              <div className="font-black text-lg">{stats.parsed}</div>
              <div className="text-[9px] uppercase tracking-widest text-slate-600">Parsed</div>
            </div>
            <div className="p-2 bg-emerald-100 text-center" data-testid="import-preview-create">
              <div className="font-black text-lg text-emerald-800">{willCreateCount}</div>
              <div className="text-[9px] uppercase tracking-widest text-emerald-700">To Create</div>
            </div>
            <div className="p-2 bg-amber-100 text-center" data-testid="import-preview-skip">
              <div className="font-black text-lg text-amber-800">{stats.will_skip}</div>
              <div className="text-[9px] uppercase tracking-widest text-amber-700">Duplicates</div>
            </div>
          </div>

          {willCreateCount > 0 && (
            <div className="flex items-center gap-2 p-2 bg-slate-50 border-2 border-slate-200">
              <span className="text-[10px] uppercase tracking-widest text-slate-600 font-black">Set rent for all →</span>
              <div className="flex items-center h-8 border-2 border-slate-400">
                <span className="px-1.5 text-slate-500 text-sm">$</span>
                <input type="number" step="1" min="0" value={bulkRent} onChange={e => setBulkRent(e.target.value)}
                  placeholder="e.g. 1500" data-testid="import-bulk-rent"
                  className="w-24 h-full px-1 text-sm outline-none"/>
              </div>
              <button onClick={applyBulkRent} data-testid="import-bulk-apply"
                className="h-8 px-3 bg-slate-800 text-white font-black uppercase text-[10px] tracking-widest">
                Apply
              </button>
              <span className="text-[10px] text-slate-500 italic">Optional — leave blank to fill in later.</span>
            </div>
          )}

          <div className="border-2 border-slate-300 max-h-72 overflow-y-auto">
            <table className="w-full text-[11px]" data-testid="import-preview-table">
              <thead className="bg-slate-100 sticky top-0">
                <tr className="text-left">
                  <th className="p-1.5">Tenant</th>
                  <th className="p-1.5">Address · Unit</th>
                  <th className="p-1.5">Move-in</th>
                  <th className="p-1.5 text-right">Deposit</th>
                  <th className="p-1.5">Monthly rent</th>
                  <th className="p-1.5"></th>
                </tr>
              </thead>
              <tbody>
                {rows.map((r, i) => (
                  <tr key={i} className={r.will_skip ? "opacity-50" : ""}>
                    <td className="p-1.5 font-bold">{r.tenant_name}</td>
                    <td className="p-1.5">{r.property_address} <span className="opacity-60">· #{r.unit || "—"}</span></td>
                    <td className="p-1.5 font-mono">{r.move_in || "—"}</td>
                    <td className="p-1.5 text-right font-mono">${(r.deposit_cents / 100).toFixed(0)}</td>
                    <td className="p-1.5">
                      {r.will_skip ? <span className="text-slate-400 italic">—</span> : (
                        <div className="flex items-center h-7 border border-slate-300 w-24">
                          <span className="px-1 text-slate-400 text-xs">$</span>
                          <input type="number" step="1" min="0" value={r.monthly_rent}
                            onChange={e => setRows(rows.map((x, ix) => ix === i ? { ...x, monthly_rent: e.target.value } : x))}
                            data-testid={`import-row-rent-${i}`}
                            className="w-full h-full text-xs outline-none px-0.5"/>
                        </div>
                      )}
                    </td>
                    <td className="p-1.5">
                      {r.will_skip
                        ? <span className="px-1.5 py-0.5 bg-amber-200 text-amber-900 font-black uppercase text-[9px] tracking-widest">Skip</span>
                        : <span className="px-1.5 py-0.5 bg-emerald-200 text-emerald-900 font-black uppercase text-[9px] tracking-widest">New</span>}
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>

          <div className="flex gap-2 pt-1">
            <button onClick={() => { setStats(null); setRows([]); setFile(null); }} data-testid="import-xlsx-reset"
              className="h-11 px-3 border-2 border-slate-400 font-black uppercase text-[11px] tracking-widest">
              Pick another file
            </button>
            <button onClick={confirm} disabled={busy || willCreateCount === 0}
              data-testid="import-xlsx-confirm"
              className="flex-1 h-11 bg-emerald-600 text-white font-black uppercase text-[11px] tracking-widest disabled:opacity-50 shadow-[3px_3px_0_#0f172a]">
              {busy ? "Importing…" : `Create ${willCreateCount} tenant${willCreateCount === 1 ? "" : "s"}`}
            </button>
          </div>
        </div>
      )}

      {importedCount > 0 && (
        <div className="mt-3 p-3 border-2 border-red-300 bg-red-50" data-testid="import-undo-section">
          <div className="text-[10px] font-black uppercase tracking-widest text-red-700">Undo appfolio imports</div>
          <div className="text-[11px] text-red-900 mt-1">
            You currently have <b>{importedCount}</b> Appfolio-imported {importedCount === 1 ? "tenant" : "tenants"} in your rent roll.
            Manually-added tenants are safe.
          </div>
          <button onClick={undoAll} disabled={busy} data-testid="import-undo-all"
            className="mt-2 h-9 px-3 bg-red-600 text-white font-black uppercase text-[10px] tracking-widest disabled:opacity-50 inline-flex items-center gap-1">
            <Trash size={12}/> Delete all {importedCount} imported
          </button>
        </div>
      )}
    </ModalShell>
  );
}


function RentRollModal({ onClose, onSaved }) {
  const [f, setF] = useState({
    property_address: "", unit: "", tenant_name: "", tenant_email: "", tenant_phone: "",
    monthly_rent_cents: 0, due_day: 1, lease_start: "", lease_end: "",
    balance_cents: 0, notes: "", active: true,
  });
  const [busy, setBusy] = useState(false);
  const save = async () => {
    if (!f.property_address || !f.tenant_name) return toast.error("Address + tenant required");
    setBusy(true);
    try {
      await api.post("/rent-roll", {
        ...f,
        monthly_rent_cents: Math.round((parseFloat(f.monthly_rent_cents) || 0) * 100),
        balance_cents: Math.round((parseFloat(f.balance_cents) || 0) * 100),
        due_day: Math.max(1, Math.min(28, parseInt(f.due_day) || 1)),
      });
      toast.success("Added"); onSaved();
    } catch (e) { toast.error(formatApiErrorDetail(e?.response?.data?.detail ?? "Failed")); }
    finally { setBusy(false); }
  };
  return (
    <ModalShell title="Add Rent Roll" bg="bg-emerald-600" onClose={onClose} testid="rent-roll-modal">
      <div className="grid grid-cols-3 gap-2">
        <input list="known-addresses" autoComplete="off" value={f.property_address} onChange={e => setF({ ...f, property_address: e.target.value })}
          placeholder="Property address *" data-testid="rr-addr"
          className="col-span-2 h-10 px-3 border-2 border-slate-300 text-sm"/>
        <input value={f.unit} onChange={e => setF({ ...f, unit: e.target.value })}
          placeholder="Unit" className="h-10 px-3 border-2 border-slate-300 text-sm"/>
      </div>
      <input value={f.tenant_name} onChange={e => setF({ ...f, tenant_name: e.target.value })}
        placeholder="Tenant name *" data-testid="rr-tenant" className="w-full h-10 px-3 border-2 border-slate-300 text-sm"/>
      <div className="grid grid-cols-2 gap-2">
        <input value={f.tenant_email} onChange={e => setF({ ...f, tenant_email: e.target.value })}
          placeholder="Tenant email" className="h-10 px-3 border-2 border-slate-300 text-sm"/>
        <input value={f.tenant_phone} onChange={e => setF({ ...f, tenant_phone: e.target.value })}
          placeholder="Tenant phone" className="h-10 px-3 border-2 border-slate-300 text-sm"/>
      </div>
      <div className="grid grid-cols-3 gap-2">
        <label className="text-[9px] uppercase tracking-widest text-slate-500 flex flex-col gap-1">Monthly rent ($)
          <input type="number" step="0.01" value={f.monthly_rent_cents}
            onChange={e => setF({ ...f, monthly_rent_cents: e.target.value })}
            data-testid="rr-rent" className="h-10 px-3 border-2 border-slate-300 text-sm"/>
        </label>
        <label className="text-[9px] uppercase tracking-widest text-slate-500 flex flex-col gap-1">Due day (1–28)
          <input type="number" min="1" max="28" value={f.due_day}
            onChange={e => setF({ ...f, due_day: e.target.value })}
            className="h-10 px-3 border-2 border-slate-300 text-sm"/>
        </label>
        <label className="text-[9px] uppercase tracking-widest text-slate-500 flex flex-col gap-1">Opening balance ($)
          <input type="number" step="0.01" value={f.balance_cents}
            onChange={e => setF({ ...f, balance_cents: e.target.value })}
            className="h-10 px-3 border-2 border-slate-300 text-sm"/>
        </label>
      </div>
      <div className="grid grid-cols-2 gap-2">
        <input value={f.lease_start} onChange={e => setF({ ...f, lease_start: e.target.value })}
          placeholder="Lease start (YYYY-MM-DD)" className="h-10 px-3 border-2 border-slate-300 text-sm"/>
        <input value={f.lease_end} onChange={e => setF({ ...f, lease_end: e.target.value })}
          placeholder="Lease end (YYYY-MM-DD)" className="h-10 px-3 border-2 border-slate-300 text-sm"/>
      </div>
      <textarea value={f.notes} onChange={e => setF({ ...f, notes: e.target.value })} rows={2}
        placeholder="Notes" className="w-full p-2 border-2 border-slate-300 text-sm"/>
      <button onClick={save} disabled={busy || !f.tenant_name || !f.property_address} data-testid="rr-save"
        className="w-full h-11 bg-emerald-600 text-white font-black uppercase tracking-widest text-sm disabled:opacity-50 shadow-[3px_3px_0_#0f172a]">
        {busy ? "Saving…" : "Add"}
      </button>
    </ModalShell>
  );
}

function PaymentModal({ entry, onClose, onSaved }) {
  const [f, setF] = useState({ amount_cents: (entry.balance_cents > 0 ? entry.balance_cents / 100 : entry.monthly_rent_cents / 100), method: "check", reference: "", note: "" });
  const [busy, setBusy] = useState(false);
  const save = async () => {
    setBusy(true);
    try {
      await api.post(`/rent-roll/${entry.id}/payment`, {
        ...f, amount_cents: Math.round((parseFloat(f.amount_cents) || 0) * 100),
      });
      toast.success("Payment recorded"); onSaved();
    } catch (e) { toast.error(formatApiErrorDetail(e?.response?.data?.detail ?? "Failed")); }
    finally { setBusy(false); }
  };
  return (
    <ModalShell title={`Record Payment — ${entry.tenant_name}`} bg="bg-emerald-600" onClose={onClose} testid="payment-modal">
      <div className="text-[11px] text-slate-600">Current balance: <b>{money(entry.balance_cents)}</b></div>
      <div className="grid grid-cols-2 gap-2">
        <label className="text-[9px] uppercase tracking-widest text-slate-500 flex flex-col gap-1">Amount ($)
          <input type="number" step="0.01" value={f.amount_cents}
            onChange={e => setF({ ...f, amount_cents: e.target.value })}
            data-testid="pmt-amount" className="h-10 px-3 border-2 border-slate-300 text-sm"/>
        </label>
        <label className="text-[9px] uppercase tracking-widest text-slate-500 flex flex-col gap-1">Method
          <select value={f.method} onChange={e => setF({ ...f, method: e.target.value })}
            className="h-10 px-3 border-2 border-slate-300 text-sm">
            <option value="check">Check</option>
            <option value="cash">Cash</option>
            <option value="ach_stub">ACH (stub)</option>
            <option value="stripe">Stripe (future)</option>
            <option value="manual">Other</option>
          </select>
        </label>
      </div>
      <input value={f.reference} onChange={e => setF({ ...f, reference: e.target.value })}
        placeholder="Reference / check #" className="w-full h-10 px-3 border-2 border-slate-300 text-sm"/>
      <textarea value={f.note} onChange={e => setF({ ...f, note: e.target.value })} rows={2}
        placeholder="Note" className="w-full p-2 border-2 border-slate-300 text-sm"/>
      <button onClick={save} disabled={busy} data-testid="pmt-save"
        className="w-full h-11 bg-emerald-600 text-white font-black uppercase tracking-widest text-sm disabled:opacity-50 shadow-[3px_3px_0_#0f172a]">
        {busy ? "Saving…" : "Record Payment"}
      </button>
    </ModalShell>
  );
}

function AchModal({ entry, onClose, onSaved }) {
  const [mode, setMode] = useState(entry.ach_configured ? "charge" : "setup");
  const [setupF, setSetupF] = useState({ bank_name: "", account_last4: "", routing_last4: "", holder_name: entry.tenant_name || "", account_type: "checking" });
  const [chargeF, setChargeF] = useState({ amount_cents: (entry.balance_cents > 0 ? entry.balance_cents / 100 : entry.monthly_rent_cents / 100), memo: `Rent ${new Date().toISOString().slice(0,7)}` });
  const [busy, setBusy] = useState(false);

  const doSetup = async () => {
    if (!setupF.bank_name || !setupF.account_last4) return toast.error("Bank + last4 required");
    setBusy(true);
    try {
      await api.post(`/rent-roll/${entry.id}/ach-setup`, setupF);
      toast.success("ACH stub configured");
      setMode("charge");
      onSaved(); // reload parent so ACH badge appears
    } catch (e) { toast.error(formatApiErrorDetail(e?.response?.data?.detail ?? "Failed")); }
    finally { setBusy(false); }
  };
  const doCharge = async () => {
    setBusy(true);
    try {
      const r = await api.post(`/rent-roll/${entry.id}/ach-charge`, {
        amount_cents: Math.round((parseFloat(chargeF.amount_cents) || 0) * 100),
        memo: chargeF.memo,
      });
      toast.success(`Stub ACH logged (${r.data.reference})`);
      onSaved();
    } catch (e) { toast.error(formatApiErrorDetail(e?.response?.data?.detail ?? "Failed")); }
    finally { setBusy(false); }
  };

  return (
    <ModalShell title={`ACH — ${entry.tenant_name}`} bg="bg-cyan-700" onClose={onClose} testid="ach-modal">
      <div className="p-2 bg-amber-50 border-l-4 border-amber-500 text-[10px] flex items-start gap-1">
        <Warning size={12} className="text-amber-600 mt-0.5"/>
        <span><b>STUB.</b> No real bank transfer will occur. Fields captured now so switching to Stripe at launch is a two-line change.</span>
      </div>
      {mode === "setup" ? (
        <>
          <input value={setupF.bank_name} onChange={e => setSetupF({ ...setupF, bank_name: e.target.value })}
            placeholder="Bank name *" data-testid="ach-bank" className="w-full h-10 px-3 border-2 border-slate-300 text-sm"/>
          <input value={setupF.holder_name} onChange={e => setSetupF({ ...setupF, holder_name: e.target.value })}
            placeholder="Account holder name" className="w-full h-10 px-3 border-2 border-slate-300 text-sm"/>
          <div className="grid grid-cols-3 gap-2">
            <input value={setupF.account_last4} onChange={e => setSetupF({ ...setupF, account_last4: e.target.value.replace(/\D/g, "").slice(0, 4) })}
              placeholder="Acct last-4 *" maxLength={4} data-testid="ach-last4"
              className="h-10 px-3 border-2 border-slate-300 text-sm"/>
            <input value={setupF.routing_last4} onChange={e => setSetupF({ ...setupF, routing_last4: e.target.value.replace(/\D/g, "").slice(0, 4) })}
              placeholder="Routing last-4" maxLength={4}
              className="h-10 px-3 border-2 border-slate-300 text-sm"/>
            <select value={setupF.account_type} onChange={e => setSetupF({ ...setupF, account_type: e.target.value })}
              className="h-10 px-3 border-2 border-slate-300 text-sm">
              <option value="checking">Checking</option>
              <option value="savings">Savings</option>
            </select>
          </div>
          <button onClick={doSetup} disabled={busy} data-testid="ach-setup-save"
            className="w-full h-11 bg-cyan-700 text-white font-black uppercase tracking-widest text-sm disabled:opacity-50 shadow-[3px_3px_0_#0f172a]">
            {busy ? "Saving…" : "Save ACH (stub)"}
          </button>
        </>
      ) : (
        <>
          <div className="text-[11px] text-slate-600">Bank on file: <b>{entry.ach_bank_name}</b> ••{entry.ach_last4}</div>
          <div className="text-[11px] text-slate-600">Balance: <b>{money(entry.balance_cents)}</b></div>
          <label className="text-[9px] uppercase tracking-widest text-slate-500 flex flex-col gap-1">Amount to pull ($)
            <input type="number" step="0.01" value={chargeF.amount_cents}
              onChange={e => setChargeF({ ...chargeF, amount_cents: e.target.value })}
              data-testid="ach-amount" className="h-10 px-3 border-2 border-slate-300 text-sm"/>
          </label>
          <input value={chargeF.memo} onChange={e => setChargeF({ ...chargeF, memo: e.target.value })}
            placeholder="Memo" className="w-full h-10 px-3 border-2 border-slate-300 text-sm"/>
          <button onClick={doCharge} disabled={busy} data-testid="ach-charge-save"
            className="w-full h-11 bg-cyan-700 text-white font-black uppercase tracking-widest text-sm disabled:opacity-50 shadow-[3px_3px_0_#0f172a]">
            {busy ? "Pulling…" : "Pull ACH (stub — no real charge)"}
          </button>
          <button onClick={() => setMode("setup")}
            className="w-full h-9 border-2 border-slate-300 text-slate-700 font-black uppercase tracking-widest text-[10px]">
            Re-enter bank info
          </button>
        </>
      )}
    </ModalShell>
  );
}

function EscalateModal({ entry, onClose, onSaved }) {
  const [f, setF] = useState({ stage: "pre_filing", next_court_date: "", docket_number: "", rental_assistance_program: "none", notes: "" });
  const [busy, setBusy] = useState(false);
  const save = async () => {
    setBusy(true);
    try {
      await api.post(`/rent-roll/${entry.id}/escalate-to-eviction`, f);
      onSaved();
    } catch (e) { toast.error(formatApiErrorDetail(e?.response?.data?.detail ?? "Failed")); }
    finally { setBusy(false); }
  };
  return (
    <ModalShell title={`Escalate — ${entry.tenant_name}`} bg="bg-red-600" onClose={onClose} testid="escalate-modal">
      <div className="text-[11px] text-slate-600">
        Balance <b>{money(entry.balance_cents)}</b> · {entry.months_behind}mo behind · will be prefilled on the new eviction case.
      </div>
      <select value={f.stage} onChange={e => setF({ ...f, stage: e.target.value })}
        className="w-full h-10 px-3 border-2 border-slate-300 text-sm">
        <option value="pre_filing">Pre-Filing</option>
        <option value="edp_mediation">EDP Mediation</option>
        <option value="filed">Filed</option>
        <option value="hearing_scheduled">Hearing Scheduled</option>
      </select>
      <div className="grid grid-cols-2 gap-2">
        <input value={f.next_court_date} onChange={e => setF({ ...f, next_court_date: e.target.value })}
          placeholder="Next court (YYYY-MM-DD)" data-testid="esc-court"
          className="h-10 px-3 border-2 border-slate-300 text-sm"/>
        <input value={f.docket_number} onChange={e => setF({ ...f, docket_number: e.target.value })}
          placeholder="Docket #" className="h-10 px-3 border-2 border-slate-300 text-sm"/>
      </div>
      <select value={f.rental_assistance_program} onChange={e => setF({ ...f, rental_assistance_program: e.target.value })}
        className="w-full h-10 px-3 border-2 border-slate-300 text-sm">
        <option value="none">No rental aid</option>
        <option value="tap">TAP</option>
        <option value="erap">ERAP</option>
        <option value="phdc">PHDC</option>
        <option value="other">Other</option>
      </select>
      <textarea value={f.notes} onChange={e => setF({ ...f, notes: e.target.value })} rows={3}
        placeholder="Notes" className="w-full p-2 border-2 border-slate-300 text-sm"/>
      <button onClick={save} disabled={busy} data-testid="esc-save"
        className="w-full h-11 bg-red-600 text-white font-black uppercase tracking-widest text-sm disabled:opacity-50 shadow-[3px_3px_0_#0f172a]">
        {busy ? "Opening…" : "Open Eviction Case"}
      </button>
    </ModalShell>
  );
}
